import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import { paramStore } from '@tvd/utils/param.store';
import type { Observable } from 'rxjs';

// Backend response interfaces matching actual API structure
export interface TokenCost {
  service: string;
  displayName: string;
  tokensPerRequest: number;
  category: 'ai' | 'messaging' | 'email' | 'other';
  active: boolean;
}

export interface TokenStatus {
  // Organisation context
  organisationId: string;

  // Current status
  balance: number; // Monthly allocation balance
  balanceExtra: number; // Extra purchased tokens
  totalBalance: number; // Sum of both buckets
  usedThisMonth: number; // Monthly usage
  lastReset: string; // Last monthly reset date

  // Auto top-up settings
  autoTopupEnabled: boolean; // Auto top-up enabled/disabled
  autoTopupLimit: number; // Monthly auto top-up limit
  autoTopupUsed: number; // Used auto top-up this month
  autoTopupRemaining: number; // Remaining auto top-up

  // System configuration
  monthlyAllocation: number; // System config (1000)

  // Usage estimates - simplified to match new backend
  estimatedRequests: {
    aiMediumRemaining: number; // Only ai_medium service now
  };

  // AI plan status
  hasAiPlan?: boolean; // Whether organization has subscribed to AI plan
}

export interface UpdateTokenSettingsRequest {
  autoTopupEnabled?: boolean;
  autoTopupLimit?: number; // 0-50000 tokens
}

export interface CreateTokenPurchaseRequest {
  tokenAmount: number; // Max 50000 tokens
}

export interface TokenPurchaseResponse {
  url: string;
  sessionId: string;
  organisationId: string;
  tokenAmount: number;
  price: number;
}

export interface UpdateTokenSettingsResponse {
  success: boolean;
  message: string;
  updates: any;
}

@Injectable({
  providedIn: 'root',
})
export class TokenApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/tokens`;
  private readonly orgId = paramStore('orgId', { as: 'path' }).current;

  /**
   * Get user's token status and pricing information
   */
  getTokenStatus(): Observable<TokenStatus> {
    const currentOrgId = this.orgId();
    // If we have orgId, use it, otherwise fallback to userId endpoint
    if (currentOrgId) {
      return this.http.get<TokenStatus>(`${this.baseUrl}/${currentOrgId}`);
    }
    // Use base endpoint which will use userId fallback on backend
    return this.http.get<TokenStatus>(this.baseUrl);
  }

  /**
   * Update user's auto top-up settings
   */
  updateTokenSettings(
    settings: UpdateTokenSettingsRequest,
  ): Observable<UpdateTokenSettingsResponse> {
    const currentOrgId = this.orgId();
    const params = currentOrgId
      ? new HttpParams().set('orgId', currentOrgId)
      : new HttpParams();
    return this.http.put<UpdateTokenSettingsResponse>(
      `${this.baseUrl}/settings`,
      settings,
      { params },
    );
  }

  /**
   * Create Stripe checkout session for token purchase
   */
  createTokenPurchaseCheckout(
    request: CreateTokenPurchaseRequest,
  ): Observable<TokenPurchaseResponse> {
    const currentOrgId = this.orgId();
    const params = currentOrgId
      ? new HttpParams().set('orgId', currentOrgId)
      : new HttpParams();
    return this.http.post<TokenPurchaseResponse>(
      `${this.baseUrl}/purchase-checkout`,
      request,
      { params },
    );
  }

  /**
   * Manual token top-up (admin only)
   */
  manualTopup(amount: number, reason?: string): Observable<any> {
    const currentOrgId = this.orgId();
    const params = currentOrgId
      ? new HttpParams().set('orgId', currentOrgId)
      : new HttpParams();
    return this.http.post(
      `${this.baseUrl}/manual-topup`,
      { amount, reason },
      { params },
    );
  }

  /**
   * Setup auto top-up payment method
   */
  setupAutoTopup(): Observable<any> {
    const currentOrgId = this.orgId();
    const params = currentOrgId
      ? new HttpParams().set('orgId', currentOrgId)
      : new HttpParams();
    return this.http.post(`${this.baseUrl}/setup-auto-topup`, {}, { params });
  }

  /**
   * Update payment method for auto top-up
   */
  updatePaymentMethod(paymentMethodId: string): Observable<any> {
    const currentOrgId = this.orgId();
    const params = currentOrgId
      ? new HttpParams().set('orgId', currentOrgId)
      : new HttpParams();
    return this.http.put(
      `${this.baseUrl}/payment-method`,
      { paymentMethodId },
      { params },
    );
  }
}
